import "@pentacode/ui/components";
import { html, type ReactiveController, type ReactiveControllerHost } from "lit";

/**
 * Refuses drops onto targets that have a reason against them: marks the target `.drop-blocked` and shows the reason
 * in a tooltip following the drag. Call `check` from `dragenter`/`dragover`, `leave` from `dragleave`, `grab` from
 * `dragstart`, and place `render()` in the host template.
 */
export class DropGuard implements ReactiveController {
    private _target: Element | null = null;

    private _reason = "";

    private _grabOffset = 0;

    private _release = () => this._block(null, "");

    private _releaseAfterDrag = (e: PointerEvent) => {
        if (!e.buttons) {
            this._release();
        }
    };

    private _dragstart = () => {
        this._grabOffset = 0;
        this._release();
    };

    constructor(private readonly _host: ReactiveControllerHost) {
        _host.addController(this);
    }

    grab(e: DragEvent) {
        this._grabOffset = e.clientY - (e.target as Element).getBoundingClientRect().top;
    }

    /** Returns `true` if the drop is blocked, in which case the caller must not accept the drag. */
    check(e: DragEvent, reason: string | null) {
        if (!reason) {
            this._release();
            return false;
        }
        e.stopPropagation();
        this._block(e.currentTarget as Element, reason);
        return true;
    }

    leave(e: DragEvent) {
        if (e.target === this._target) {
            this._release();
        }
    }

    render() {
        return html`
            <ptc-ui-pointer-tooltip
                class="drop-blocked-tooltip"
                .open=${!!this._target}
                .distance=${this._grabOffset + 8}
            >
                ${this._reason}
            </ptc-ui-pointer-tooltip>
        `;
    }

    hostConnected() {
        window.addEventListener("dragstart", this._dragstart, { capture: true });
        window.addEventListener("dragend", this._release, { capture: true });
        window.addEventListener("drop", this._release, { capture: true });
    }

    hostDisconnected() {
        this._release();
        window.removeEventListener("dragstart", this._dragstart, { capture: true });
        window.removeEventListener("dragend", this._release, { capture: true });
        window.removeEventListener("drop", this._release, { capture: true });
    }

    private _block(target: Element | null, reason: string) {
        if (target === this._target && (!target || reason === this._reason)) {
            return;
        }
        this._target?.classList.remove("drop-blocked");
        target?.classList.add("drop-blocked");
        if (!this._target && target) {
            window.addEventListener("pointermove", this._releaseAfterDrag, { capture: true });
        } else if (this._target && !target) {
            window.removeEventListener("pointermove", this._releaseAfterDrag, { capture: true });
        }
        this._target = target;
        if (target) {
            this._reason = reason;
        }
        this._host.requestUpdate();
    }
}
